import { logger } from '@ninetailed/experience.js-shared';
import { z } from 'zod';

const Audience = z.object({
  id: z.string(),
  name: z.string().optional(),
  description: z.string().optional()
});

const Config = z.object({
  distribution: z.array(z.number()).optional().default([0.5, 0.5]),
  traffic: z.number().optional().default(0),
  components: z.array(z.object({
    baseline: z.object({
      id: z.string().default('')
    }),
    variants: z.array(z.object({
      id: z.string().default(''),
      hidden: z.boolean().default(false)
    }))
  })).optional().default([{
    baseline: {
      id: ''
    },
    variants: [{
      id: '',
      hidden: false
    }]
  }])
});

const Variant = z.object({
  id: z.string()
}).catchall(z.unknown());

/**
 * Zod helper for parsing arrays and ignore items not specified in the schema
 *
 * @param zodUnion - union of known types
 *
 * @example
 * const binaryArraySchema = arrayIgnoreUnknown(z.union([z.literal('0'), z.literal('1')]))
 * type BinaryArray = z.TypeOf<typeof binaryArraySchema>
 *
 * const binaryArray: BinaryArray = binaryArraySchema.parse(['0', '1', '2', '0'])
 * console.log(binaryArray) // ['0', '1', '0']
 */
function zodArrayIgnoreUnknown(zodType) {
  const isKnownItem = item => zodType.safeParse(item).success;
  return z.preprocess(val => toSafeArray(val).filter(isKnownItem), z.array(zodType));
}
function toSafeArray(item) {
  if (isArray(item)) {
    return item;
  }
  return [item];
}
function isArray(item) {
  return Array.isArray(item);
}

const ExperienceSchema = z.object({
  id: z.string(),
  /**
   * The name of the experience (Short Text)
   */
  name: z.string(),
  /**
   * The description of the experience (Short Text)
   */
  description: z.string().optional(),
  /**
   * The type if the experience (nt_experiment | nt_personalization)
   */
  type: z.union([z.string().regex(/^nt_experiment$/g), z.string().regex(/^nt_personalization$/g)]),
  /**
   * The config of the experience (JSON)
   */
  config: Config.default({}),
  /**
   * The audience of the experience (Audience)
   */
  audience: Audience.optional().nullable(),
  /**
   * All used variants of the experience (References to other Content Types)
   */
  variants: zodArrayIgnoreUnknown(Variant).default([])
});
const parse$1 = input => {
  const output = ExperienceSchema.parse(input);
  return Object.assign(Object.assign({}, output), {
    variants: input.variants
  });
};
const safeParse$1 = input => {
  const output = ExperienceSchema.safeParse(input);
  if (!output.success) {
    return output;
  }
  return Object.assign(Object.assign({}, output), {
    data: Object.assign(Object.assign({}, output.data), {
      variants: input.variants
    })
  });
};
const Experience = Object.assign(Object.assign({}, ExperienceSchema), {
  parse: parse$1,
  safeParse: safeParse$1
});

const ExperimentSchema = ExperienceSchema.extend({
  type: z.string().regex(/^nt_experiment$/g)
});
const parse = input => {
  const output = ExperimentSchema.parse(input);
  return Object.assign(Object.assign({}, output), {
    variants: input.variants
  });
};
const safeParse = input => {
  const output = ExperimentSchema.safeParse(input);
  if (!output.success) {
    return output;
  }
  return Object.assign(Object.assign({}, output), {
    data: Object.assign(Object.assign({}, output.data), {
      variants: input.variants
    })
  });
};
const Experiment = Object.assign(Object.assign({}, ExperimentSchema), {
  parse,
  safeParse
});

class ExperienceMapper {
  static isExperienceEntry(experience) {
    return Experience.safeParse(experience).success;
  }
  static mapExperience(experience) {
    const parsedExperience = Experience.safeParse(experience);
    if (!parsedExperience.success) {
      logger.warn('[Ninetailed ExperienceMapper]', 'Error parsing experience', parsedExperience.error.format());
      throw new Error(`[Ninetailed ExperienceMapper] The Experience Input is not valid. Please filter data first with "ExperienceMapper.isExperienceEntry".\n${JSON.stringify(parsedExperience.error.format(), null, 2)}`);
    }
    const {
      id,
      type,
      name,
      description,
      audience,
      config,
      variants
    } = parsedExperience.data;
    const {
      components,
      traffic
    } = config;
    return Object.assign(Object.assign(Object.assign({
      id,
      type: type,
      name
    }, description ? {
      description
    } : {}), audience ? {
      audience
    } : {}), {
      trafficAllocation: traffic,
      distribution: config.distribution.map((percentage, index) => ({
        index,
        start: config.distribution.slice(0, index).reduce((a, b) => a + b, 0),
        end: config.distribution.slice(0, index + 1).reduce((a, b) => a + b, 0)
      })),
      components: components.map(component => ({
        baseline: component.baseline,
        variants: component.variants.map(variantRef => {
          if (variantRef.hidden) {
            return variantRef;
          }
          const matchingVariant = variants.find(variant => variant.id === variantRef.id);
          return matchingVariant !== null && matchingVariant !== void 0 ? matchingVariant : null;
        }).filter(variant => variant !== null)
      }))
    });
  }
  static isExperimentEntry(experiment) {
    return Experiment.safeParse(experiment).success;
  }
  static mapExperiment(experiment) {
    return ExperienceMapper.mapExperience(experiment);
  }
}

export { Audience, Config, Experience, ExperienceMapper, Experiment, Variant };
